import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutUser } from 'api-client';
import { type AuthAccountWithPassType, resendVerificationEmail } from 'api-client/accounts';
import useAccountStore from 'stores/useAccountStore';
import sameVersion from 'stores/compare/versions';
import { capitalize } from 'utils/string-utils';
import { ODSAlert, ODSButton, ODSGroup, ODSLoginPortal, ODSSpinner } from 'bc-ods';
import type { EmailVerificationResult } from 'bc-model';
import { EMAIL_VERIFICATION_RESULTS } from 'bc-model';
import { includeBasePath } from 'utils/url-utils';

const messages = defineMessages({
  pendingEmailSentAlert: {
    id: 'pendingEmailSent.alert',
    defaultMessage: 'Please verify your email before you can sign in.',
  },
  pendingEmailSentInfo: {
    id: 'pendingEmailSent.info',
    defaultMessage:
      "You should have received an email at the address you provided. Please check your inbox and click the verify email button. If you haven't received it yet, be sure to check your spam folder.",
  },
  pendingEmailResentAlert: {
    id: 'pendingEmailResent.alert',
    defaultMessage: 'Email verification has been sent.',
  },
  pendingEmailResentInfo: {
    id: 'pendingEmailResent.info',
    defaultMessage:
      "You'll receive an email shortly at the email address you provided. Please check your inbox and click the verify email button. If you haven't received it yet, be sure to check your spam folder.",
  },
  pendingEmailTokenExpiredAlert: {
    id: 'pendingEmailTokenExpired.alert',
    defaultMessage: 'The verification token has expired, please request a new verification email.',
  },
  pendingEmailTokenExpiredInfo: {
    id: 'pendingEmailTokenExpired.info',
    defaultMessage: ' ',
  },
  pendingEmailAlreadyVerifiedAlert: {
    id: 'pendingEmailTokenAlreadyVerifiedAlert.alert',
    defaultMessage: 'Your account is already verified.',
  },
  pendingEmailAlreadyVerifiedInfo: {
    id: 'pendingEmailTokenAlreadyVerified.info',
    defaultMessage: ' ',
  },
  emailButtonResend: {
    id: 'emailButton.resend',
    defaultMessage: 'Resend verification email',
  },
  emailButtonBack: {
    id: 'emailButton.back',
    defaultMessage: 'Back',
  },
  emailButtonReturnToSignIn: {
    id: 'emailButton.returnToSignIn',
    defaultMessage: 'Return to sign in',
  },
  emailButtonContinue: {
    id: 'emailButton.continue',
    defaultMessage: 'Continue',
  },
});

const ResendVerificationEmailButton = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const sent = !!params.get('sent');
  const user = useAccountStore(
    ({ account }) => account ?? ({} as AuthAccountWithPassType),
    sameVersion
  );
  const isLoggedIn = !!user?.id;
  const [sending, setSending] = useState<boolean>(false);

  const handleResendEmailClick = useCallback(async () => {
    setSending(true);
    await resendVerificationEmail();
    // add a delay to prevent flashing and give the UX of a successful action
    setTimeout(() => {
      navigate(`/verify-email?result=${EMAIL_VERIFICATION_RESULTS.resent}`);
      setSending(false);
    }, 1000);
  }, [navigate, setSending]);

  if (!isLoggedIn) return null;
  return (
    <ODSButton
      tag="button"
      onClick={handleResendEmailClick}
      outline={sent}
      cta={!sent}
      disabled={sending}
      fullWidth
      data-testid="resend-verification-email"
    >
      {sending && <ODSSpinner size="sm" />}
      {!sending && <FormattedMessage {...messages.emailButtonResend} />}
    </ODSButton>
  );
};

const parseConfirmationResult = (rawResult: string | null): EmailVerificationResult => {
  const result = EMAIL_VERIFICATION_RESULTS[rawResult ?? ''];
  if (!result) {
    return 'invalid';
  }
  return result;
};

const VerifyEmail: FC = () => {
  const query = new URLSearchParams(window.location.search);
  const rawResult = query.get('result');
  const result = parseConfirmationResult(rawResult);

  if (result === 'success') {
    window.location.href = includeBasePath('/');
    return null;
  }

  const handleLogout = () => {
    logoutUser();
  };

  let alertColor = 'danger';
  if (result === 'resent' || result === 'alreadyVerified') {
    alertColor = 'success';
  }

  let resultFormMessage = result;
  if (!['resent', 'tokenExpired', 'alreadyVerified'].includes(result)) resultFormMessage = 'sent';

  const alertMessage = `pendingEmail${capitalize(resultFormMessage)}Alert`;
  const infoMessage = `pendingEmail${capitalize(resultFormMessage)}Info`;

  return (
    <ODSLoginPortal data-testid="account-pending-form">
      <ODSGroup stacked spacing="lg">
        {alertMessage && (
          <ODSAlert color={alertColor}>
            <FormattedMessage {...messages[alertMessage]} />
          </ODSAlert>
        )}
        {infoMessage && (
          <div className="text-secondary small">
            <FormattedMessage {...messages[infoMessage]} />
          </div>
        )}
        <ODSGroup fullWidth>
          <ODSButton quiet onClick={handleLogout} data-testid="go-to-login" className="flex-grow-1">
            <FormattedMessage {...messages.emailButtonBack} />
          </ODSButton>
          {result !== 'alreadyVerified' && <ResendVerificationEmailButton />}
        </ODSGroup>
      </ODSGroup>
    </ODSLoginPortal>
  );
};

export default VerifyEmail;
